import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmDialog from "../components/ConfirmDialog";
import Toast from "../components/Toast";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { formatDate } from "../services/datetime";
import { fetchPublicPlans, pickCycle, planText } from "../services/signupService";
import {
  cancelPendingPlan,
  changePlan,
  fetchSubscription,
  validateBusinessPromo,
} from "../services/subscriptionService";
import { CycleSwitcher, Money, PlanCard } from "./Pricing";

const STATUS_STYLE = {
  active: "bg-accent-wash text-accent-text",
  trialing: "bg-warning-wash text-warning-text",
  pending_payment: "bg-warning-wash text-warning-text",
  trial_expired: "bg-danger-wash text-danger-text",
  payment_required: "bg-danger-wash text-danger-text",
};

/** What the business is on now: plan, type, status, trial end or price snapshot. */
function CurrentPlan({ subscription }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const plan = subscription.plan;
  const billing = subscription.billing;
  const zone = subscription.timezone;
  // A pending request shows on its own card; here, what access follows meanwhile.
  const status = subscription.status === "pending_payment" ? subscription.access_status : subscription.status;

  return (
    <section className="card mb-6 p-5" aria-labelledby="current-plan">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="current-plan" className="text-card-title font-bold text-ink">
          {plan ? planText(plan.name, lang) : t("billing.noPlan")}
        </h2>
        {plan && (
          <span className="rounded-full bg-raised px-2 py-0.5 text-xs font-semibold text-ink-2">
            {t(`billing.types.${plan.type}`)}
          </span>
        )}
        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_STYLE[status] ?? ""}`}>
          {t(`billing.statuses.${status}`)}
        </span>
      </div>

      {subscription.days_left != null && (
        <p className="mt-2 text-sm text-ink-2">
          {subscription.days_left > 0
            ? t("billing.trialDaysLeft", { count: subscription.days_left })
            : t("billing.trialEndsToday")}{" "}
          ·{" "}
          {t("billing.trialEndsOn", {
            date: formatDate(subscription.trial_ends_at, lang, { timeZone: zone }),
          })}
        </p>
      )}

      {subscription.gated && subscription.gate_code !== "payment_pending" && (
        <p className="mt-2 text-sm font-semibold text-danger-text">
          {subscription.gate_code === "plan_required"
            ? t("billing.planRequiredHint")
            : t("billing.trialEndedHint")}
        </p>
      )}

      {billing && (
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-ink-2">{t("billing.cycle")}</dt>
            <dd className="font-semibold text-ink">{planText(billing.billing_cycle_name, lang) || "—"}</dd>
          </div>
          <div>
            <dt className="text-ink-2">{t("billing.total")}</dt>
            <dd className="font-semibold text-ink">
              <Money amount={billing.total} currency={{ code: billing.currency, decimals: 2 }} />
            </dd>
          </div>
          <div>
            <dt className="text-ink-2">{t("billing.periodEnds")}</dt>
            <dd className="font-semibold text-ink">
              {billing.current_period_ends_at
                ? formatDate(billing.current_period_ends_at, lang, { timeZone: zone })
                : "—"}
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
}

/** A paid plan waiting for an admin to confirm the payment; the owner may withdraw it. */
function PendingRequest({ pending, gated, setup, isOwner, onCancel }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  return (
    <section
      className="mb-8 rounded-card border border-warning-line bg-warning-wash p-5"
      aria-labelledby="pending-request"
      role="status"
    >
      <h2 id="pending-request" className="text-card-title font-bold text-warning-text">
        {t("billing.awaitingPayment")}
      </h2>
      <p className="mt-1 text-sm text-ink">
        {t("billing.pendingFor", { plan: planText(pending.plan?.name, lang) || "—" })}
        {pending.billing_cycle_name ? ` · ${planText(pending.billing_cycle_name, lang)}` : ""}
      </p>
      <p className="mt-2 text-sm text-ink">
        {t("billing.amountDue")}{" "}
        <Money amount={pending.total} currency={pending.currency} className="font-bold" />
        {pending.promo_code ? (
          <span className="text-ink-2"> · {t("billing.promoApplied", { code: pending.promo_code })}</span>
        ) : null}
      </p>
      <p className="mt-2 text-xs text-ink-2">
        {setup ? t("billing.setupHint") : gated ? t("billing.pendingGatedHint") : t("billing.pendingHint")}
      </p>
      {isOwner && (
        <button type="button" className="btn-secondary mt-3" onClick={onCancel}>
          {t("billing.cancelRequest")}
        </button>
      )}
    </section>
  );
}

/**
 * Switching plan. A paid plan shows its quote for the chosen cycle and takes a
 * promo code (checked against the business endpoint, priced in the business's
 * currency); it becomes a request waiting for payment. A free plan applies at
 * once. Every amount comes from the server.
 */
function ChoosePlanDialog({ plan, cycleId, busy, error, onConfirm, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const paid = plan.type === "paid";
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoState, setPromoState] = useState({ busy: false, error: "" });
  const quote = promo?.quote ?? (paid ? plan.prices?.[cycleId] : null);
  const currency = quote?.currency ?? plan.currency;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const apply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setPromoState({ busy: true, error: "" });
    try {
      const promoQuote = await validateBusinessPromo({ code: code.trim(), planId: plan.id, cycleId });
      setPromo({ code: promoQuote.promo?.code ?? code.trim(), quote: promoQuote });
      setPromoState({ busy: false, error: "" });
    } catch (err) {
      const fields = apiFieldErrors(err);
      setPromo(null);
      setPromoState({
        busy: false,
        error:
          fields.promo_code?.[0] ??
          fields.billing_cycle_id?.[0] ??
          apiErrorMessage(err, t("signup.promoFailed")),
      });
    }
  };

  const message = plan.trial_days
    ? t("billing.confirmTrial", { count: plan.trial_days })
    : paid
      ? t("billing.confirmPaid")
      : t("billing.confirmBody");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="choose-plan-title"
        className="w-full max-w-md rounded-xl bg-surface shadow-xl"
      >
        <div className="px-5 py-4">
          <h2 id="choose-plan-title" className="text-sm font-semibold">
            {t("billing.confirmTitle", { plan: planText(plan.name, lang) })}
          </h2>
          <p className="mt-2 text-sm text-ink-2">{message}</p>

          {paid && quote && (
            <dl className="mt-3 space-y-1 rounded-lg border border-line px-3 py-2 text-sm">
              {Number(quote.cycle_discount_amount) > 0 && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-2">
                    {t("signup.cycleDiscount", { percent: Number(quote.cycle_discount_percent) })}
                  </dt>
                  <dd>
                    −<Money amount={quote.cycle_discount_amount} currency={currency} />
                  </dd>
                </div>
              )}
              {promo && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-2">
                    {t("signup.promoDiscount")} <span className="font-mono">{promo.code}</span>
                  </dt>
                  <dd>
                    −<Money amount={quote.promo_discount_amount} currency={currency} />
                  </dd>
                </div>
              )}
              <div className="flex justify-between gap-3 font-bold">
                <dt>{t("billing.amountDue")}</dt>
                <dd>
                  <Money amount={quote.total} currency={currency} />
                </dd>
              </div>
            </dl>
          )}

          {/* Promo codes apply to paid plans only. */}
          {paid && (
            <form className="mt-3" onSubmit={apply} noValidate>
              <label className="field-label" htmlFor="billing_promo">
                {t("signup.promoLabel")}
              </label>
              <div className="flex gap-2">
                <input
                  id="billing_promo"
                  dir="ltr"
                  className={`field-input flex-1 font-mono uppercase ${promoState.error ? "border-danger" : ""}`}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (promo) setPromo(null);
                  }}
                  placeholder={t("signup.promoPlaceholder")}
                  maxLength={32}
                  disabled={busy}
                  aria-invalid={!!promoState.error}
                  aria-describedby={promoState.error ? "billing_promo_error" : undefined}
                />
                <button
                  type="submit"
                  className="btn-secondary"
                  disabled={busy || promoState.busy || !code.trim()}
                >
                  {promoState.busy ? t("signup.promoApplying") : t("signup.promoApply")}
                </button>
              </div>
              {promoState.error && (
                <p id="billing_promo_error" className="field-error">
                  {promoState.error}
                </p>
              )}
            </form>
          )}

          {error && (
            <div className="mt-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || promoState.busy}
            onClick={() => onConfirm(promo?.code ?? null)}
          >
            {busy ? t("common.saving") : paid ? t("billing.requestPlan") : t("billing.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Plans & billing: the current plan and trial, a paid plan waiting for payment
 * confirmation, and every plan the pricing page offers (in the business's own
 * currency) to switch to. Only the owner can change plan. A paid plan becomes
 * a request until an admin confirms the payment. Reachable while gated.
 */
export default function Billing({ user, onChanged }) {
  const { t } = useTranslation();
  const isOwner = Boolean(user?.is_owner);
  const [subscription, setSubscription] = useState(null);
  const [data, setData] = useState(null);
  const [cycleId, setCycleId] = useState(null);
  const [error, setError] = useState("");
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const closeToast = useCallback(() => setToast(null), []);

  const load = useCallback(() => {
    setError("");
    // Signed in, so the server prices each plan in the business's currency
    // and says which trial is no longer available to it.
    Promise.all([fetchSubscription(), fetchPublicPlans()])
      .then(([sub, plans]) => {
        setSubscription(sub);
        setData(plans);
        setCycleId(
          (current) =>
            current ??
            sub.billing?.billing_cycle_id ??
            plans.defaultCycleId ??
            plans.billingCycles[0]?.id ??
            null
        );
      })
      .catch((err) => setError(apiErrorMessage(err, t("billing.loadFailed"))));
  }, [t]);

  useEffect(load, [load]);

  const afterChange = (result, fallback) => {
    setSubscription(result.data);
    setToast({ message: result.message || fallback, type: "success" });
    onChanged?.();
    load();
  };

  const confirm = async (promoCode) => {
    if (!choice) return;
    setBusy(true);
    setDialogError("");
    try {
      const cycle = choice.type === "paid" ? pickCycle(choice, cycleId, data?.defaultCycleId) : null;
      const result = await changePlan({ planId: choice.id, cycleId: cycle, promoCode });
      setChoice(null);
      afterChange(result, t("billing.changed"));
    } catch (err) {
      const fields = apiFieldErrors(err);
      setDialogError(
        fields.plan_id?.[0] ??
          fields.billing_cycle_id?.[0] ??
          fields.promo_code?.[0] ??
          apiErrorMessage(err, t("billing.changeFailed"))
      );
    } finally {
      setBusy(false);
    }
  };

  const cancelRequest = async () => {
    setBusy(true);
    setDialogError("");
    try {
      const result = await cancelPendingPlan();
      setCancelOpen(false);
      afterChange(result, t("billing.requestCancelled"));
    } catch (err) {
      setDialogError(apiFieldErrors(err).payment?.[0] ?? apiErrorMessage(err, t("billing.changeFailed")));
    } finally {
      setBusy(false);
    }
  };

  const currentPlanId = subscription?.plan?.id;
  const pending = subscription?.pending ?? null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-page-title font-bold text-ink">{t("billing.title")}</h1>
        <p className="mt-1 text-ink-2">{t("billing.subtitle")}</p>
      </div>

      {error ? (
        <div className="card p-6 text-center" role="alert">
          <p className="mb-4 text-danger-text">{error}</p>
          <button type="button" className="btn-primary" onClick={load}>
            {t("pricing.retry")}
          </button>
        </div>
      ) : !subscription || !data ? (
        <div className="motion-safe:animate-pulse" aria-busy="true" aria-label={t("pricing.loading")}>
          <div className="mb-8 h-28 rounded-card bg-line" />
          <div className="h-96 rounded-card bg-line" />
        </div>
      ) : (
        <>
          <CurrentPlan subscription={subscription} />

          {pending && (
            <PendingRequest
              pending={pending}
              gated={subscription.gated}
              setup={Boolean(subscription.setup_mode)}
              isOwner={isOwner}
              onCancel={() => {
                setDialogError("");
                setCancelOpen(true);
              }}
            />
          )}

          <h2 className="mb-2 text-section-title font-bold text-ink">{t("billing.plans")}</h2>
          <p className="mb-4 text-sm text-ink-2">
            {isOwner ? t("billing.paidNeedsConfirmation") : t("billing.ownerOnly")}
          </p>

          {data.billingCycles.length > 1 && (
            <div className="mb-8 flex justify-center">
              <CycleSwitcher cycles={data.billingCycles} value={cycleId} onChange={setCycleId} />
            </div>
          )}

          {data.plans.length === 0 ? (
            <p className="card p-6 text-center text-ink-2">{t("pricing.empty")}</p>
          ) : (
            <div className="flex flex-wrap items-stretch justify-center gap-6 pt-4">
              {data.plans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  cycleId={cycleId}
                  onChoose={(picked) => {
                    setDialogError("");
                    setChoice(picked);
                  }}
                  busy={busy || !isOwner}
                  // Same plan and (for paid) same cycle, and usable: nothing to change.
                  current={
                    plan.id === currentPlanId &&
                    !subscription.gated &&
                    (plan.type !== "paid" || subscription.billing?.billing_cycle_id === cycleId)
                  }
                />
              ))}
            </div>
          )}
        </>
      )}

      {choice && (
        <ChoosePlanDialog
          // A new cycle or plan starts a fresh promo check.
          key={`${choice.id}:${cycleId}`}
          plan={choice}
          cycleId={choice.type === "paid" ? pickCycle(choice, cycleId, data?.defaultCycleId) : null}
          busy={busy}
          error={dialogError}
          onConfirm={confirm}
          onClose={() => !busy && setChoice(null)}
        />
      )}

      <ConfirmDialog
        open={cancelOpen}
        title={t("billing.cancelRequestTitle")}
        message={t("billing.cancelRequestBody")}
        confirmLabel={t("billing.cancelRequest")}
        busy={busy}
        error={dialogError}
        onConfirm={cancelRequest}
        onClose={() => !busy && setCancelOpen(false)}
      />

      <Toast toast={toast} onClose={closeToast} />
    </div>
  );
}
